import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Card, Descriptions, Empty, Spin, Table, Tag } from 'antd';
import moment from 'moment';

const API = process.env.REACT_APP_API_URL;

const STATUS_CONFIG = {
  in_use: { label: 'IN USE', color: 'blue' },
  in_spare_stock: { label: 'IN SPARE STOCK', color: 'green' },
  to_be_cleaned: { label: 'TO BE CLEANED', color: 'orange' },
  inactive: { label: 'INACTIVE', color: 'default' }
};

const ACTION_LABELS = {
  label_created: 'Label Created',
  assigned_to_area: 'Assigned to Area',
  sent_for_cleaning: 'Sent for Cleaning',
  marked_cleaned_and_assigned: 'Cleaned and Assigned',
  hvac_inactivated: 'Inactivated',
  hvac_reactivated: 'Reactivated'
};

const getStatusKey = (record) => {
  if (!record) return 'to_be_cleaned';
  if (record.is_active === false || record.is_retired === true) {
    return 'inactive';
  }
  if (record.status === 'to_be_cleaned') return 'to_be_cleaned';
  if (record.status === 'in_use') {
    if (String(record.current_area || '').toLowerCase() === 'spare stock') {
      return 'in_spare_stock';
    }
    return 'in_use';
  }
  return 'to_be_cleaned';
};

const fmtTs = (ts) =>
  ts
    ? moment(ts).utcOffset('+05:30').format('DD MMM YYYY HH:mm:ss') + ' IST'
    : '—';

const parseDetails = (v) => {
  if (!v) return {};
  return typeof v === 'string' ? JSON.parse(v) : v;
};

const SectionTitle = ({ children }) => (
  <h4
    style={{
      fontSize: 11,
      fontWeight: 700,
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      margin: '16px 0 8px'
    }}
  >
    {children}
  </h4>
);

const formatLogDetails = (action, details) => {
  if (!details) return '—';
  let d;
  try {
    d = parseDetails(details);
  } catch (_) {
    return String(details);
  }
  switch (action) {
    case 'label_created':
      return d.location ? `Label generated at ${d.location}` : '—';
    case 'assigned_to_area':
      return `From: ${d.from_area || '—'} → To: ${d.to_area || '—'}`;
    case 'sent_for_cleaning':
      return `Removed from: ${d.from_area || '—'}`;
    case 'marked_cleaned_and_assigned':
      return `Cleaned and assigned to: ${d.to_area || '—'}`;
    case 'hvac_inactivated':
      return `Reason: ${d.reason || '—'}`;
    case 'hvac_reactivated':
      return `Note: ${d.note || '—'} · Now at Spare Stock`;
    default:
      try {
        return JSON.stringify(d);
      } catch (_) {
        return '—';
      }
  }
};

const logColumns = [
  {
    title: 'Timestamp (IST)',
    dataIndex: 'timestamp',
    key: 'timestamp',
    width: 190,
    render: fmtTs
  },
  {
    title: 'Activity',
    dataIndex: 'action',
    key: 'action',
    width: 170,
    render: (v) => ACTION_LABELS[v] || v
  },
  {
    title: 'By',
    dataIndex: 'performed_by_name',
    key: 'performed_by_name',
    width: 150
  },
  {
    title: 'Details',
    dataIndex: 'details',
    key: 'details',
    render: (v, row) => formatLogDetails(row.action, v)
  }
];

const HvacPublicPage = () => {
  const { id } = useParams();
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    fetch(`${API}/hvac-filters-public/${encodeURIComponent(id)}`)
      .then((r) => r.json())
      .then((res) => {
        if (res?.success && res.data) setFilter(res.data);
        else setError(res?.message || `No HVAC filter found for "${id}".`);
      })
      .catch(() => setError('Could not reach the server.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" tip="Loading HVAC filter details..." />
      </div>
    );
  }

  if (error || !filter) {
    return (
      <Card style={{ maxWidth: 600, margin: '40px auto' }}>
        <Empty description={error || `No HVAC filter found for "${id}".`} />
      </Card>
    );
  }

  const statusKey = getStatusKey(filter);
  const statusCfg = STATUS_CONFIG[statusKey];
  const logs = filter.activity_logs || [];
  const isInUse = statusKey === 'in_use' || statusKey === 'in_spare_stock';
  const isInactive = statusKey === 'inactive';
  const inactivationDetails = filter.inactivation_details || null;

  // Find last "sent_for_cleaning" log for the Last Assignment section
  const lastSentForCleaning =
    statusKey === 'to_be_cleaned'
      ? logs.find((l) => l.action === 'sent_for_cleaning')
      : null;
  const lastSentDetails = lastSentForCleaning
    ? parseDetails(lastSentForCleaning.details)
    : null;

  return (
    <div style={{ maxWidth: 760, margin: '24px auto', padding: '0 12px' }}>
      <Card>
        {/* Decommissioned banner */}
        {isInactive && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="This HVAC filter has been decommissioned."
            description={
              <span>
                <strong>Reason:</strong> {inactivationDetails?.reason || '—'}
                &nbsp;&nbsp;
                <strong>By:</strong> {inactivationDetails?.by_name || '—'}
                &nbsp;&nbsp;
                <strong>At:</strong> {fmtTs(inactivationDetails?.at)}
              </span>
            }
          />
        )}

        {/* HVAC identifier */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div
            style={{
              fontFamily: 'Courier New, monospace',
              fontSize: 28,
              fontWeight: 700,
              color: '#c91761',
              letterSpacing: 2
            }}
          >
            {filter.hvac_label_number}
          </div>
          <Tag color={statusCfg.color} style={{ marginTop: 8, fontSize: 13 }}>
            {statusCfg.label}
          </Tag>
        </div>

        {/* HVAC Filter Details */}
        <SectionTitle>HVAC Filter Details</SectionTitle>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="HVAC Label">
            <span style={{ fontFamily: 'monospace' }}>
              {filter.hvac_label_number}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="In-house Number">
            {filter.inhouse_number || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
          </Descriptions.Item>
        </Descriptions>

        {/* Current Assignment (in_use only — manufacturing or spare) */}
        {isInUse && (
          <>
            <SectionTitle>Current Assignment</SectionTitle>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Current Area">
                {filter.current_area ? (
                  <Tag
                    color={
                      String(filter.current_area).toLowerCase() ===
                      'spare stock'
                        ? 'green'
                        : 'blue'
                    }
                  >
                    {filter.current_area}
                  </Tag>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Assigned At">
                {fmtTs(filter.allocated_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Cleaned At">
                {filter.cleaned_at ? fmtTs(filter.cleaned_at) : 'Never'}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}

        {/* Last Assignment (to_be_cleaned only) */}
        {statusKey === 'to_be_cleaned' && (
          <>
            <SectionTitle>Last Assignment</SectionTitle>
            {lastSentForCleaning ? (
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Last Installed At">
                  {lastSentDetails?.from_area || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Removed At">
                  {fmtTs(lastSentForCleaning.timestamp)}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Empty
                description="No prior assignment recorded."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </>
        )}

        {/* Creation Details */}
        <SectionTitle>Creation Details</SectionTitle>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Original Location">
            {filter.original_location || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Created By">
            {filter.created_by_name || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {fmtTs(filter.createdAt)}
          </Descriptions.Item>
        </Descriptions>

        {/* Full Activity History */}
        <SectionTitle>Activity History</SectionTitle>
        <Table
          columns={logColumns}
          dataSource={logs}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                description="No activity recorded for this HVAC filter yet."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }}
        />
      </Card>
    </div>
  );
};

export default HvacPublicPage;
